import {DestinyDatabase} from "@Database/Database";
import {generateMockDefinitions} from "./DestinyDefinitionsMock";
import {DestinyDefinitionsWorkerMessages} from "./DestinyDefinitionsWorkerMessages";
import {BaseLogger} from "../../BaseLogger";

/** We will store a hashmap of definition types to promises here. If a definition is requested multiple times, everything after the first request will be returned the pending request. */
const pendingRequests = {};
/** We will store a copy of the updated manifest returned from initAndUpdateOnce(). If initAndUpdateOnce is called multiple times, everything after the first request will be returned the pending request. */
let newManifestDataPromise = undefined;

const logger = new BaseLogger("[DEFINITIONS]");

export class DestinyDefinitionsLoader
{
	/**
	 * @type Function
	 */
	static messageSender = null;
	
	static setMessageSender(messageSender)
	{
		this.messageSender = messageSender;
	}
	
	/**
	 * Send a message back to the requesting thread
	 * @param {any} definitions
	 */
	static sendDefinitions(definitions)
	{
		// Stringify the results before sending them for perf reasons
		// https://nolanlawson.com/2016/02/29/high-performance-web-worker-messages/
		this.messageSender({
			name: DestinyDefinitionsWorkerMessages.LOADED_DEFINITIONS,
			detail: {
				definitions: JSON.stringify(definitions)
			}
		});
	}

	/**
	 * Add definitions to the database
	 * @param {String} definitionsType The name of the definition type being added
	 * @param {Array} definitions The definitions themselves
	 */
	static async addToDb(definitionsType, definitions)
	{
		// Convert definitions to expected table format
		const mapped = Object.keys(definitions).map(hash => ({
			hash,
			def: definitions[hash]
		}));

		await this.db.table(definitionsType).bulkPut(mapped);

		return this.defObjectFromArray(mapped);
	}

	/**
	 * Downloads a file from the server, given a path that originates from the manifest
	 * @param {any} manifest Manifest data
	 * @param {String} definitionsType The name of the definition type being added
	 * @param {String} locale The locale to load definitions for
	 */
	static async downloadFile(manifest, definitionsType, locale)
	{
		const path = manifest?.jsonWorldComponentContentPaths[locale][definitionsType];

		return fetch(path).then(r =>
		{
			if (!r.ok)
			{
				throw new Error("Could not download manifest at " + path);
			}

			return r.json();
		});
	}

	/**
	 * Attempts to download definitions from the web and store them
	 * @param {any} manifest Manifest data
	 * @param {String} definitionsType The name of the definition type being added
	 * @param {any} locale The locale to download
	 */
	static async tryFetchExternal(manifest, definitionsType, locale)
	{
		const result = await this.downloadFile(manifest, definitionsType, locale);

		return this.addToDb(definitionsType, result);
	}

	/**
	 * Attempts to fetch definitions from IndexedDB
	 * @param {any} definitionsType The definition type to fetch
	 */
	static async tryFetchFromDb(definitionsType)
	{
		const table = this.db.table(definitionsType);
		if (table)
		{
			const collection = table.toCollection();
			const arr = await collection.toArray();

			return this.defObjectFromArray(arr);
		}
	}

	static defObjectFromArray(arr)
	{
		return arr.reduce((acc, val) =>
		{
			acc[val.hash] = val;

			return acc;
		}, {});
	}

	static isValidDefinitionsObject(obj)
	{
		return obj !== undefined
			&& obj !== null
			&& Object.keys(obj).length > 0;
	}

	/**
	 * Loads definitions from whatever source is needed in order of performance (or if manifest is old, get new ones).
	 * @param {String} definitionsType The name of the definition type being added
	 * @param {any} manifest Manifest data
	 * @param {any} locale The locale to download
	 * @param {boolean} mock If true, mock definitions rather than downloading
	 */
	static async isomorphicLoad(definitionsType, manifest, locale, mock)
	{
		let result;
		
		logger.logVerbose("Definitions requested: ", definitionsType);

		// If something already tried to fetch this externally, wait until that is finished and then fetch it from the database.
		// Typically happens with multiple renders, or multiple components requesting the same definitions at the same time
		if (definitionsType in pendingRequests)
		{
			logger.logVerbose("Returning pending for: ", definitionsType);
			return await pendingRequests[definitionsType];
		}

		if (manifest?.isCurrent)
		{
			logger.logVerbose("Fetching from DB: ", definitionsType);
			const promise = this.tryFetchFromDb(definitionsType);
			result = await promise;
		}

		if (!this.isValidDefinitionsObject(result))
		{
			logger.logVerbose("Failed to fetch from DB: ", definitionsType);
			if (mock)
			{
				result = generateMockDefinitions()
			}
			else
			{
				const promise = this.tryFetchExternal(manifest?.manifest, definitionsType, locale);
				pendingRequests[definitionsType] = promise;
				result = await promise;
			}
		}

		if (!this.isValidDefinitionsObject(result))
		{
			throw new Error(`Could not load ${definitionsType} from local database or externally.`);
		}

		delete pendingRequests[definitionsType];

		return result;
	}
	
	static sendManifestUpdateMessage(isUpdating)
	{
		this.messageSender({
			name: DestinyDefinitionsWorkerMessages.UPDATING_MANIFEST,
			detail: isUpdating
		});
	}

	/**
	 * Initialize database and set schema, check version and update manifest if needed
	 * Function will return an existing promise if it is not returned yet
	 * (This is to prevent the db.close being called by a different component in the middle of this request)
	 */
	static initAndUpdateOnce(manifest, locale)
	{
		if (newManifestDataPromise)
		{
			return newManifestDataPromise;
		}

		newManifestDataPromise = new Promise(async (resolve, reject) =>
		{
			this.db = await DestinyDatabase;

			try
			{				
				if (manifest && manifest.isCurrent === false)
				{
					// Alert the consumer that we're updating so we don't try to access the DB while it's closed
					this.sendManifestUpdateMessage(true);

					// If the manifest needs to be updated, we should ditch all of the existing loaded defs
					// otherwise we'll start returning those once we update the manifest
					await this.db.delete();

					// We need to reopen the DB after we delete it
					await this.db.open();

					resolve({
						version: manifest.manifest.version,
						locale: locale
					});

					newManifestDataPromise = undefined;
				} else 
				{
					resolve();
				}
			}
			catch (e)
			{
				setTimeout(() =>
				{
					throw e
				});

				reject(e);
			}
		})

		return newManifestDataPromise;
	}

	/**
	 * Load using data sent into the worker
	 * @param {any} detail
	 */
	static async load(detail)
	{
		return new Promise(async (resolve, reject) =>
		{
			let definitionsToReturn = undefined;

			const manifest = detail.manifest;
			const definitionsTypes = detail.types;
			const locale = detail.locale;

			let errorPopulatingDefinitions = false;

			let newManifestData;

			try
			{
				newManifestData = await this.initAndUpdateOnce(manifest, locale);

				// Alert the consumer that we're done, so the consumer can start accessing the DB again
				this.sendManifestUpdateMessage(false);

				// For every type requested, load it either from the DB or from the server
				const promises = definitionsTypes.map(dt => this.isomorphicLoad(dt, manifest, locale, detail.mock));

				const resultData = await Promise.all(promises);

				// For each resulting definition set, add them to one big object
				definitionsToReturn = definitionsTypes.reduce((all, curr, i) =>
				{
					all[curr] = resultData[i];

					return all;
				}, {});
			}
			catch (e)
			{
				errorPopulatingDefinitions = true;

				// This is a little trick that allows errors thrown inside promises to propogate out to the worker's onerror function (rather than being uncaught promise rejections)
				setTimeout(() =>
				{
					throw e
				});

				reject(e);
			}

			// We only want to update the manifest in the database if it needs it AND there was not an issue updating the actual databse
			if (newManifestData && !errorPopulatingDefinitions)
			{
				// Remove other records from the manifest table - we only want one version in here at a time
				await this.db.table("manifest").clear();

				await this.db.table("manifest").put(newManifestData);
			}

			if (definitionsToReturn)
			{
				resolve(definitionsToReturn);
			}
		});
	}
}

DestinyDefinitionsLoader.db = null;
DestinyDefinitionsLoader.schemaInitialized = false;